<template>
  <div class="sidd-message-list-container">
    <ScrollArea
      ref="scrollContainer"
      class="sidd-message-list"
      :class="{ 'sidd-message-list--loading': isLoading }"
    >
      <div class="sidd-message-list__content">
        <div class="sidd-message-list__messages">
          <div v-if="historicalMessages && historicalMessages.length > 0">
            <MessageBubble
              v-for="message in historicalMessages"
              :key="message.id"
              :message="message"
              class="sidd-message-list__message sidd-message-list__message--historical"
              @follow-up-question-click="handleFollowUpQuestionClick"
            />
            <Separator class="sidd-message-list__separator" />
          </div>

          <MessageBubble
            v-for="message in messages"
            :key="message.id"
            :message="message"
            class="sidd-message-list__message"
            @follow-up-question-click="handleFollowUpQuestionClick"
          />

          <TypingIndicator
            v-if="showTyping"
            class="sidd-message-list__typing"
          />
        </div>

        <div
          v-if="isLoading && messages.length === 0"
          class="sidd-message-list__loading"
        >
          <div class="sidd-message-list__loading-content">
            <div class="flex flex-col gap-2">
              <Skeleton class="h-4 w-full" />
              <Skeleton class="h-4 w-3/4" />
              <Skeleton class="h-4 w-1/2" />
            </div>
          </div>
        </div>

        <div
          v-if="!isLoading && messages.length === 0 && !showWelcome"
          class="sidd-message-list__empty"
        >
          <p class="sidd-message-list__empty-text">Start a conversation...</p>
        </div>
      </div>
    </ScrollArea>

    <div v-if="showWelcome" class="sidd-message-list__welcome-wrapper">
      <WelcomeMessages
        :is-loading="isLoading"
        :config="siddDuoConfig"
        @question-click="handleSuggestionClick"
      />
    </div>

    <ScrollToBottom :show="showScrollButton" @click="handleScrollToBottom" />
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch, computed, onMounted, onUnmounted } from 'vue'
import { ScrollArea } from '@/lib/ui/scroll-area'
import { Separator } from '@/lib/ui/separator'
import { Skeleton } from '@/lib/ui/skeleton'
import MessageBubble from '../MessageBubble/MessageBubble.vue'
import TypingIndicator from '../TypingIndicator/TypingIndicator.vue'
import WelcomeMessages from '../WelcomeMessages/WelcomeMessages.vue'
import ScrollToBottom from '../ScrollToBottom'
import type { IMessageListProps, IMessageListEmits } from './types'

const props = withDefaults(defineProps<IMessageListProps>(), {
  isLoading: false,
  showTyping: false,
  autoScroll: true,
})

const emit = defineEmits<IMessageListEmits>()

const scrollContainer = ref<InstanceType<typeof ScrollArea>>()
const viewportElement = ref<HTMLElement | null>(null)
const showScrollButton = ref(false)
const isUserAtBottom = ref(true)
const SCROLL_THRESHOLD = 25
const previousMessageCount = ref(0)
const isInitialLoad = ref(true)

const showWelcome = computed(() => {
  return (
    props.messages.length === 0 &&
    (props.historicalMessages?.length ?? 0) === 0 &&
    !props.isLoading
  )
})

const getViewport = () => {
  return viewportElement.value
}

const checkIfAtBottom = () => {
  const viewport = getViewport()
  if (!viewport) return true

  const { scrollTop, scrollHeight, clientHeight } = viewport
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight
  return distanceFromBottom < SCROLL_THRESHOLD
}

const hasOverflowContent = () => {
  const viewport = getViewport()
  if (!viewport) return false

  const { scrollHeight, clientHeight } = viewport
  return scrollHeight > clientHeight + SCROLL_THRESHOLD
}

const updateScrollButtonVisibility = () => {
  const atBottom = checkIfAtBottom()
  const hasOverflow = hasOverflowContent()

  isUserAtBottom.value = atBottom
  showScrollButton.value = hasOverflow && !atBottom && props.messages.length > 0
}

const handleScroll = () => {
  updateScrollButtonVisibility()
}

const scrollToBottom = async (smooth = true) => {
  if (!scrollContainer.value) return

  await nextTick()

  const viewport = getViewport()
  if (viewport) {
    viewport.scrollTo({
      top: viewport.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto',
    })
    await nextTick()
    updateScrollButtonVisibility()
  }
}

const scrollToTop = async (smooth = true) => {
  if (!scrollContainer.value) return

  await nextTick()

  const viewport = getViewport()
  if (viewport) {
    viewport.scrollTo({
      top: 0,
      behavior: smooth ? 'smooth' : 'auto',
    })
  }
}

const handleScrollToBottom = () => {
  scrollToBottom(true)
}

const handleSuggestionClick = (question: string) => {
  if (props.onQuestionClick) {
    props.onQuestionClick(question, true)
  } else {
    emit('suggestion-click', question)
  }
}

const handleFollowUpQuestionClick = (
  question: string,
  parentMessageId: string
) => {
  if (props.onQuestionClick) {
    props.onQuestionClick(question, true, true, parentMessageId)
  } else {
    emit('suggestion-click', question)
  }
}

watch(
  () => props.messages,
  async newMessages => {
    const currentCount = newMessages.length

    if (currentCount > previousMessageCount.value) {
      previousMessageCount.value = currentCount
    } else {
      await nextTick()
      if (isUserAtBottom.value && props.autoScroll) {
        await scrollToBottom(false)
      }
      updateScrollButtonVisibility()
    }
  }
)

watch(
  () => props.showTyping,
  async newValue => {
    await nextTick()
    if (newValue) {
      await scrollToBottom(false)
    }
    updateScrollButtonVisibility()
  }
)

onMounted(async () => {
  // Cache the viewport element reference
  viewportElement.value =
    scrollContainer.value?.$el?.querySelector(
      '[data-slot="scroll-area-viewport"]'
    ) ?? null

  if (viewportElement.value) {
    viewportElement.value.addEventListener('scroll', handleScroll)
  }
  previousMessageCount.value = props.messages.length

  // Scroll to bottom on initial load if there are messages
  if (
    isInitialLoad.value &&
    (props.messages.length > 0 ||
      (props.historicalMessages && props.historicalMessages.length > 0))
  ) {
    await nextTick()
    await scrollToBottom(false)
    isInitialLoad.value = false
  }

  updateScrollButtonVisibility()
})

onUnmounted(() => {
  if (viewportElement.value) {
    viewportElement.value.removeEventListener('scroll', handleScroll)
  }
  viewportElement.value = null
})

defineExpose({
  scrollToBottom,
  scrollToTop,
  checkIfAtBottom,
  isUserAtBottom: () => isUserAtBottom.value,
})
</script>

<style>
.sidd-message-list-container {
  position: relative;
  height: 100%;
}

.sidd-message-list {
  height: 100%;
  background: hsl(var(--background));
}

.sidd-message-list__content {
  display: flex;
  flex-direction: column;
}

.sidd-message-list__welcome-wrapper {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
}

.sidd-message-list__messages {
  padding: 20px;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-grow: 1;
}

.sidd-message-list__typing {
  align-self: flex-start;
}

.sidd-message-list__loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
}

.sidd-message-list__loading-content {
  max-width: 300px;
  width: 100%;
}

.sidd-message-list__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
}

.sidd-message-list__message--historical {
  opacity: 0.7;
}

.sidd-message-list__separator {
  margin: 24px 0;
}

.sidd-message-list__empty-text {
  color: hsl(var(--muted-foreground));
  font-size: 0.875em;
  text-align: center;
  margin: 0;
}
</style>
